import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import uniq from 'lodash/uniq';

import { UnitName, Viewer } from '$shared/components';
import { useCurrentUserInfo, useDecryptKey, useDesktop, useProfileInfo } from '$hooks';
import { postsActions, postsSelector } from '$store/posts';
import { imActions, imAsPeerIdSelector, imSelector } from '$store/im';
import { ViewerWidgetProps } from './ViewerWidget.interface';
import { useDispatch, useSelector } from 'react-redux';
import { AttachItem, AttachType, MessageModel } from '$store/models';
import { ViewerItem } from '$shared/components/Viewer/VieverItem/ViewerItem.interface';
import {
  attachmentsToViewerItems,
  attachmentToViewerItem,
  getImageSizeByUrl,
} from '$utils';
import { goTo, goBack, showModal } from '$navigation/helper';
import { ACCESS_DENIED, AUTH_REQUIRED } from '$shared/constants/pages';
import { mainSelector } from '$store/main';
import { profileActions } from '$store/profile';
import { groupsActions } from '$store/groups';
import { battleSelector } from '$store/battle';
import { authSelector } from '$store/auth';
import { paidSelector } from '$store/paid';

function messagesToViewerItems(messages: MessageModel[], userId: number): ViewerItem[] {
  const items: ViewerItem[] = [];
  messages
    .filter(
      (item) =>
        !!item.attachments &&
        item.attachments.length > 0 &&
        (!item.isHidden || item.fromId === userId),
    )
    .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
    .forEach((message) => {
      items.push(
        ...((message.attachments || []) as AttachItem[])
          .filter((attach) => [AttachType.PHOTO, AttachType.VIDEO].includes(attach.type))
          .map(
            (attach, part) =>
              attachmentToViewerItem(
                attach,
                message.decryptKey || '',
                message.id,
                part || undefined,
              ) as ViewerItem,
          ),
      );
    });
  return items;
}

export const ViewerWidget: FC<ViewerWidgetProps> = ({
  object,
  selectedId,
  instanceId,
  partId,
}) => {
  const [type, objectId] = useMemo(() => {
    const m = object.match(/^([a-z]+)(\d+)$/);
    return [m?.[1] || 'post', m?.[2] ? +m[2] : 0];
  }, [object]);
  const slideCounter = useRef(0);
  const isDesktop = useDesktop();
  const { posts } = useSelector(postsSelector);
  const { history } = useSelector(imSelector);
  const { messages: purchases } = useSelector(paidSelector);
  const asPeerId = useSelector(imAsPeerIdSelector);
  const { isLoading } = useSelector(authSelector);
  const { users, groups } = useSelector(mainSelector);
  const profileInfo = useProfileInfo(objectId);
  const { content: battleContent } = useSelector(battleSelector);
  const currentUser = useCurrentUserInfo();
  const dispatch = useDispatch();
  const startVisibleTime = useMemo(Date.now, []);
  const [seenIds, setSeenIds] = useState<number[]>([]);

  const decryptKey = useDecryptKey(posts[objectId]);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (type === 'post') {
      if (!posts[objectId]) {
        dispatch(postsActions.loadPost(objectId));
      }
    } else if (type === 'history') {
      if (!history[objectId]) {
        dispatch(
          imActions.loadChat({ peerId: objectId, ...(asPeerId ? { asPeerId } : {}) }),
        );
      }
    } else if (type === 'profile') {
      if (!users[objectId] || !profileInfo) {
        dispatch(
          profileActions.loadProfile({
            ownerId: objectId,
          }),
        );
      } else {
        setSize({
          width: profileInfo.photoWidth,
          height: profileInfo.photoHeight,
        });
      }
    } else if (type === 'group') {
      if (!groups[objectId]) {
        dispatch(groupsActions.loadGroupInfo(objectId));
      } else {
        getImageSizeByUrl(groups[objectId].photoLarge).then(setSize);
      }
    }
  }, [dispatch, object, history, posts, users, objectId, type, selectedId]);

  const items = useMemo((): ViewerItem[] => {
    if (type === 'battle') {
      return attachmentsToViewerItems(battleContent, '');
    } else if (type === 'post') {
      return attachmentsToViewerItems(
        (posts[objectId]?.attachments || []) as AttachItem[],
        decryptKey,
      );
    } else if (type === 'profile' || type === 'group') {
      const unit = users[objectId] || groups[objectId];
      if (size.width && size.height && unit.photoId) {
        return [
          {
            id: unit.photoId,
            width: size.width,
            height: size.height,
            source: unit.photoLarge,
            thumbnail: unit.thumbnail || unit.photoLarge,
          },
        ];
      } else {
        return [];
      }
    } else if (type === 'history') {
      return messagesToViewerItems(
        Object.values(history[objectId] || {}),
        currentUser.id,
      );
    } else if (type === 'paid') {
      return messagesToViewerItems(purchases, currentUser.id);
    } else {
      return [];
    }
  }, [
    posts,
    object,
    history,
    purchases,
    decryptKey,
    type,
    objectId,
    size,
    users,
    battleContent,
    currentUser.id,
  ]);

  useEffect(() => {
    return () => {
      type === 'post' &&
        dispatch(
          postsActions.viewPost({
            postId: objectId,
            mediaViewCount: seenIds.length,
            mediaViewDuration: Date.now() - startVisibleTime,
          }),
        );
    };
  }, [startVisibleTime, objectId, type, seenIds]);

  const index = useMemo(() => {
    const found = items.findIndex((i) => {
      return i.id === selectedId && i.instance === instanceId && i.part === partId;
    });
    if (found > -1) {
      return found;
    } else {
      return 0;
    }
  }, [items, selectedId, instanceId, partId]);

  useEffect(() => {
    items.length && setSeenIds(uniq([...seenIds, items[index].id]));
  }, [index, items]);

  useEffect(() => {
    slideCounter.current++;
  }, [index]);

  const title =
    type === 'profile' ? (
      <UnitName unit={users[objectId]} />
    ) : type === 'group' ? (
      <UnitName unit={groups[objectId]} />
    ) : undefined;

  if (!items.length || !items[index]) {
    return <Viewer onChange={() => {}} mobile={!isDesktop} active={index} items={[]} />;
  }

  if (!items[index]?.source && !isLoading) {
    goTo(currentUser.id ? ACCESS_DENIED : AUTH_REQUIRED);
    return null;
  }

  return (
    <Viewer
      onClose={() => goBack(false, isDesktop ? slideCounter.current : 1)}
      title={title}
      onChange={(index) => {
        const { id, instance, part } = items[index];
        showModal(
          ['photos', `${type}${objectId}`, id, instance, part].filter(Boolean).join('+'),
          !isDesktop,
        );
      }}
      mobile={!isDesktop}
      active={index}
      items={items}
    />
  );
};
